/**
 * ============================================================================
 * ADMIN FINANCE DASHBOARD PAGE
 * ============================================================================
 * Same structure and UX as Admin Transactions: hero, stats bento, financial
 * overview, withdrawals list with filters and pagination.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAdminStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Textarea from '../../components/common/Textarea';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

/** Normalize backend withdrawal (snake_case + nested user) for display */
function normalizeWithdrawal(w) {
  if (!w) return null;
  const user = w.user || {};
  return {
    ...w,
    userName: user.full_name ?? user.fullName ?? user.name ?? 'N/A',
    amount: w.net_amount ?? w.amount,
    createdAt: w.requested_at ?? w.created_at ?? w.requestedAt ?? w.createdAt,
    referenceNumber: w.reference_number ?? w.referenceNumber,
  };
}

const AdminFinance = () => {
  const navigate = useNavigate();
  const {
    loadFinancialOverview,
    listPendingWithdrawals,
    listWithdrawals,
    processWithdrawal,
    rejectWithdrawal,
    loading,
    withdrawals,
  } = useAdmin();
  const { filters, setFilters, pagination, setPagination } = useAdminStore();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, language } = useI18n();

  const [financials, setFinancials] = useState(null);
  const [withdrawalsList, setWithdrawalsList] = useState([]);
  const [withdrawalsPagination, setWithdrawalsPagination] = useState(null);
  const [statusFilter, setStatusFilter] = useState(filters?.withdrawalStatus ?? 'pending');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [gatewayRef, setGatewayRef] = useState('');
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [overviewRes, withdrawalsRes] = await Promise.all([
          loadFinancialOverview({ timeRange: '30d' }),
          listWithdrawals
            ? listWithdrawals({
                status: statusFilter || undefined,
                page: pagination?.page || 1,
                limit: pagination?.pageSize || 20,
              })
            : listPendingWithdrawals({
                page: pagination?.page || 1,
                limit: pagination?.pageSize || 20,
              }),
        ]);
        setFinancials(overviewRes?.data ?? overviewRes);
        const wData = withdrawalsRes?.data ?? withdrawalsRes;
        setWithdrawalsList(Array.isArray(wData?.withdrawals) ? wData.withdrawals : (Array.isArray(withdrawals) ? withdrawals : []));
        if (wData?.pagination) setWithdrawalsPagination(wData.pagination);
      } catch (error) {
        showError(
          error.message ||
            (language === 'fr' ? 'Échec du chargement des données' : 'Failed to load data')
        );
        console.error('Load financials error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    loadFinancialOverview,
    listPendingWithdrawals,
    listWithdrawals,
    statusFilter,
    showError,
    language,
    pagination?.page,
    pagination?.pageSize,
  ]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleFilterChange = (key, value) => {
    setStatusFilter(value);
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const overview = financials?.data ?? financials ?? {};
  const revenue = overview.revenue ?? {};
  const escrowBalance = overview.escrowBalance ?? 0;
  const totalWalletBalance = overview.totalWalletBalance ?? {};
  const pendingWithdrawalsSummary = overview.pendingWithdrawals ?? { count: 0, totalAmount: 0 };

  const stats = {
    revenue: revenue.totalRevenue ?? 0,
    escrow: typeof escrowBalance === 'string' ? escrowBalance : String(escrowBalance ?? 0),
    wallet: totalWalletBalance.total ?? totalWalletBalance.available ?? 0,
    pendingCount: pendingWithdrawalsSummary.count ?? withdrawalsList.length,
  };

  const list = Array.isArray(withdrawalsList) ? withdrawalsList : (Array.isArray(withdrawals) ? withdrawals : []);
  const totalFromPagination = withdrawalsPagination?.total ?? list.length;
  const showPagination = totalFromPagination > (pagination?.pageSize ?? 20);

  const handleWithdrawalAction = async () => {
    if (!selectedWithdrawal) return;
    try {
      if (actionType === 'approve') {
        await processWithdrawal(selectedWithdrawal.id, {
          gatewayReference: gatewayRef,
          notes: actionReason || undefined,
        });
        showSuccess(language === 'fr' ? 'Retrait traité' : 'Withdrawal processed');
      } else {
        await rejectWithdrawal(selectedWithdrawal.id, actionReason);
        showSuccess(language === 'fr' ? 'Retrait rejeté' : 'Withdrawal rejected');
      }
      setShowActionModal(false);
      setSelectedWithdrawal(null);
      setGatewayRef('');
      setActionReason('');
      const wData = await listWithdrawals({
        status: statusFilter || undefined,
        page: 1,
        limit: pagination?.pageSize || 20,
      });
      setWithdrawalsList(Array.isArray(wData?.withdrawals) ? wData.withdrawals : []);
      if (wData?.pagination) setWithdrawalsPagination(wData.pagination);
      const overviewRes = await loadFinancialOverview({ timeRange: '30d' });
      setFinancials(overviewRes?.data ?? overviewRes);
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr' ? "Échec de l'action" : 'Failed to perform action')
      );
    }
  };

  const statusOptions = [
    { value: 'pending', label: language === 'fr' ? 'En attente' : 'Pending' },
    { value: 'completed', label: language === 'fr' ? 'Complété' : 'Completed' },
    { value: 'rejected', label: language === 'fr' ? 'Rejeté' : 'Rejected' },
    { value: '', label: language === 'fr' ? 'Tous' : 'All' },
  ];

  if (isLoading || (loading && !financials)) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des finances...' : 'Loading finances...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              💰 {language === 'fr' ? 'Tableau de bord financier' : 'Finance Dashboard'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Surveillez les finances et les retraits'
                : 'Monitor finances and withdrawals'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats - Bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {[
                {
                  icon: '📊',
                  label: language === 'fr' ? 'Revenus' : 'Revenue',
                  value: formatCurrency(stats.revenue),
                },
                {
                  icon: '🔒',
                  label: language === 'fr' ? 'Escrow' : 'Escrow',
                  value: formatCurrency(stats.escrow),
                },
                {
                  icon: '💳',
                  label: language === 'fr' ? 'Portefeuilles' : 'Wallets',
                  value: formatCurrency(stats.wallet),
                },
                {
                  icon: '⏳',
                  label: language === 'fr' ? 'En attente' : 'Pending',
                  value: stats.pendingCount,
                },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Revenue breakdown */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  📊 {language === 'fr' ? 'Répartition des revenus' : 'Revenue Breakdown'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <div className="space-y-3">
                  {[
                    {
                      label: language === 'fr' ? 'Frais plateforme' : 'Platform fees',
                      value: revenue.platformFees ?? 0,
                    },
                    {
                      label: language === 'fr' ? 'Frais de retrait' : 'Withdrawal fees',
                      value: revenue.withdrawalFees ?? 0,
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center p-3 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a]"
                    >
                      <span className="text-[#a3a3a3] font-medium">{item.label}</span>
                      <span className="font-bold text-[#e5e5e5]">
                        {formatCurrency(item.value)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-[#2a2a2a] pt-4 mt-4 flex justify-between items-center">
                    <span className="font-bold text-[#e5e5e5]">
                      {language === 'fr' ? 'Total' : 'Total'}
                    </span>
                    <span className="text-lg font-black text-[#22c55e]">
                      {formatCurrency(stats.revenue)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Withdrawals - Filters & List */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  💸 {language === 'fr' ? 'Retraits' : 'Withdrawals'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <div className="transactions-wt-form-row">
                  <div className="transactions-wt-form-select">
                    <Select
                      value={statusFilter || ''}
                      onChange={(e) => handleFilterChange('withdrawalStatus', e.target.value || 'pending')}
                      options={statusOptions}
                    />
                  </div>
                </div>
              </div>
            </div>

            {list.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">💸</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucun retrait' : 'No withdrawals found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Aucun retrait ne correspond au filtre'
                    : 'No withdrawals match the selected filter'}
                </p>
              </div>
            ) : (
              <>
                <div className="dashboard-wt-transactions transactions-wt-list">
                  {list.map((withdrawal, index) => {
                    const w = normalizeWithdrawal(withdrawal);
                    return (
                      <div
                        key={withdrawal.id}
                        className="dashboard-wt-transaction-wrapper dashboard-wt-card"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="dashboard-wt-card-content">
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/80 to-green-600/80 flex items-center justify-center text-xl shadow-lg flex-shrink-0">
                              💸
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="font-bold text-[#e5e5e5]">
                                  {w.userName} · {formatCurrency(w.amount)}
                                </h3>
                                <span className="text-xs px-2 py-0.5 rounded bg-[#2a2a2a] text-[#a3a3a3]">
                                  {(withdrawal.status || '').toUpperCase()}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#737373]">
                                {w.referenceNumber && (
                                  <span>{w.referenceNumber}</span>
                                )}
                                <span>
                                  {w.createdAt &&
                                    new Date(w.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            {withdrawal.status === 'pending' && (
                              <div className="flex flex-wrap gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-primary"
                                  onClick={() => {
                                    setSelectedWithdrawal(withdrawal);
                                    setActionType('approve');
                                    setGatewayRef('');
                                    setShowActionModal(true);
                                  }}
                                >
                                  {language === 'fr' ? 'Approuver' : 'Approve'} ✓
                                </button>
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-secondary border-red-500/50 text-red-400 hover:bg-red-500/10"
                                  onClick={() => {
                                    setSelectedWithdrawal(withdrawal);
                                    setActionType('reject');
                                    setActionReason('');
                                    setShowActionModal(true);
                                  }}
                                >
                                  {language === 'fr' ? 'Rejeter' : 'Reject'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showPagination && (
                  <div className="transactions-wt-pagination dashboard-wt-card">
                    <div className="transactions-wt-pagination-inner">
                      <span className="transactions-wt-pagination-text">
                        {language === 'fr' ? 'Affichage' : 'Showing'}{' '}
                        {(pagination.page - 1) * (pagination.pageSize || 20) + 1}{' '}
                        {language === 'fr' ? 'à' : 'to'}{' '}
                        {Math.min(
                          pagination.page * (pagination.pageSize || 20),
                          totalFromPagination
                        )}{' '}
                        {language === 'fr' ? 'sur' : 'of'}{' '}
                        {totalFromPagination}
                      </span>
                      <div className="transactions-wt-pagination-btns">
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page - 1 })
                          }
                          disabled={pagination.page <= 1}
                        >
                          {language === 'fr' ? 'Précédent' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page + 1 })
                          }
                          disabled={
                            pagination.page * (pagination.pageSize || 20) >=
                            totalFromPagination
                          }
                        >
                          {language === 'fr' ? 'Suivant' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Action Modal */}
      <Modal
        isOpen={showActionModal}
        onClose={() => {
          setShowActionModal(false);
          setSelectedWithdrawal(null);
          setActionReason('');
          setGatewayRef('');
        }}
        title={
          actionType === 'approve'
            ? language === 'fr'
              ? 'Approuver le retrait'
              : 'Approve Withdrawal'
            : language === 'fr'
            ? 'Rejeter le retrait'
            : 'Reject Withdrawal'
        }
      >
        <div className="space-y-4">
          {selectedWithdrawal && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <p className="font-bold text-gray-900 dark:text-white">
                {normalizeWithdrawal(selectedWithdrawal).userName}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {formatCurrency(
                  selectedWithdrawal.net_amount ??
                    selectedWithdrawal.amount ??
                    0
                )}
              </p>
            </div>
          )}
          {actionType === 'approve' && (
            <Input
              label={language === 'fr' ? 'Référence passerelle' : 'Gateway reference'}
              value={gatewayRef}
              onChange={(e) => setGatewayRef(e.target.value)}
              placeholder="e.g. TX-12345"
            />
          )}
          {actionType === 'reject' && (
            <Textarea
              label={
                language === 'fr' ? 'Raison du rejet (requis)' : 'Rejection reason (required)'
              }
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              placeholder={
                language === 'fr'
                  ? 'Expliquez pourquoi ce retrait est rejeté...'
                  : 'Explain why this withdrawal is rejected...'
              }
              rows={4}
              required
            />
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowActionModal(false);
                setSelectedWithdrawal(null);
                setActionReason('');
                setGatewayRef('');
              }}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="gradient"
              fullWidth
              onClick={handleWithdrawalAction}
              disabled={actionType === 'reject' && !actionReason.trim()}
              className={
                actionType === 'reject'
                  ? 'bg-gradient-to-r from-red-600 to-pink-600'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600'
              }
            >
              {actionType === 'approve'
                ? language === 'fr'
                  ? 'Approuver'
                  : 'Approve'
                : language === 'fr'
                ? 'Rejeter'
                : 'Reject'}
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminFinance;
